/**
 * Upcoming/Explore feed — only show games users can actually lock.
 * Hides stale finals, if-necessary playoff placeholders, and past tip-offs.
 */

const PRE_GAME_STATUSES = new Set([
  'NS',
  'SCHEDULED',
  'PRE',
  'PRE-GAME',
  'NOT STARTED',
  'TIMED',
  'PREGAME',
]);

const TERMINAL_STATUS_TOKENS = [
  'FINAL',
  'FT',
  'POSTPONED',
  'CANCELED',
  'CANCELLED',
  'SUSPENDED',
  'ABANDONED',
  'AWARDED',
  'UNNECESSARY',
  'IF NECESSARY',
  'DELAYED', // not lockable until rescheduled
];

export function normalizeGameStatus(status) {
  return String(status || '').trim().toUpperCase();
}

export function gameStartMs(gameOrIso) {
  const iso = typeof gameOrIso === 'string' ? gameOrIso : gameOrIso?.startTime;
  if (iso == null || String(iso).trim() === '') return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

export function isLiveGameStatus(status) {
  const s = normalizeGameStatus(status);
  if (!s) return false;
  if (s.startsWith('LIVE')) return true;
  if (/^Q\d/.test(s) || s === 'HT' || s === 'HALFTIME' || s === 'HALF TIME') return true;
  if (/^\d{1,2}:\d{2}/.test(s)) return true;
  if (s === 'IN_PLAY' || s === 'IN PLAY' || s === 'INPROGRESS' || s === 'IN PROGRESS') return true;
  if (s === '1H' || s === '2H' || s === 'ET' || s === 'PEN' || s === 'PENALTIES') return true;
  if (/^(TOP|BOT|MID|END)\b/.test(s)) return true;
  if (/INNING/.test(s) || /^\d+(ST|ND|RD|TH)\s/.test(s)) return true;
  if (/^P\d\b/.test(s)) return true;
  if (s === 'OT' || s === 'OVERTIME' || s === 'AOT' || s === 'SO') return true;
  return false;
}

/** Live row stuck on old scores after tip-off — treat as finished, not live. */
const STALE_LIVE_MS = 6 * 60 * 60_000;

export function isStaleFinishedGame(game, opts = {}) {
  if (!game) return false;
  if (isTerminalGameStatus(game.status)) return true;

  const now = opts.now ?? Date.now();
  const graceMs = opts.graceMs ?? 5 * 60_000;
  const start = gameStartMs(game);
  if (start == null) return false;

  if (hasFinalScores(game) && !isLiveGameStatus(game.status)) {
    return start < now - graceMs;
  }

  if (isLiveGameStatus(game.status) && hasFinalScores(game)) {
    const maxLiveMs = opts.maxLiveMs ?? STALE_LIVE_MS;
    if (start < now - maxLiveMs) return true;
  }

  return false;
}

/** Live board / strip — canonical status check, excludes finals and pre-game. */
export function isDisplayLiveGame(gameOrStatus) {
  const game = typeof gameOrStatus === 'string' ? { status: gameOrStatus } : gameOrStatus;
  const status = typeof gameOrStatus === 'string' ? gameOrStatus : gameOrStatus?.status;
  if (!status || isTerminalGameStatus(status)) return false;
  if (isStaleFinishedGame(game)) return false;
  return isLiveGameStatus(status);
}

export function isTerminalGameStatus(status) {
  const s = normalizeGameStatus(status);
  if (!s) return false;
  if (s === 'FT' || s === 'FINAL') return true;
  // Rain/delay while still in progress (e.g. LIVE DELAYED) — not terminal.
  if (s.startsWith('LIVE') && !s.includes('FINAL') && !s.includes('FT')) {
    if (s.includes('DELAY') || s.includes('RAIN')) return false;
  }
  return TERMINAL_STATUS_TOKENS.some((token) => s.includes(token));
}

export function isPreGameStatus(status) {
  const s = normalizeGameStatus(status);
  if (!s) return false;
  if (PRE_GAME_STATUSES.has(s)) return true;
  return s.includes('SCHEDULED') || s.includes('NOT STARTED');
}

export function isIfNecessaryPlaceholder(game) {
  if (!game || game.ifNecessary === true) return true;
  const haystack = [
    game.status,
    game.league,
    game.gameLabel,
    game.gameSubLabel,
    game.seriesText,
    game.lockabilityNote,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return (
    haystack.includes('if necessary')
    || haystack.includes('if needed')
    || haystack.includes('unnecessary')
  );
}

export function hasFinalScores(game) {
  const a = game?.scoreA;
  const b = game?.scoreB;
  if (a == null || b == null) return false;
  const na = Number(a);
  const nb = Number(b);
  if (Number.isNaN(na) || Number.isNaN(nb)) return false;
  const start = gameStartMs(game);
  if (!start) return false;
  return start < Date.now();
}

export function upcomingWindowBounds(days = 7) {
  const startToday = new Date();
  startToday.setHours(0, 0, 0, 0);
  const endWindow = new Date(startToday);
  endWindow.setDate(endWindow.getDate() + (Math.max(1, days) - 1));
  endWindow.setHours(23, 59, 59, 999);
  return { startToday, endWindow };
}

/**
 * @param {object} game
 * @param {{ now?: number, graceMs?: number, windowDays?: number }} [opts]
 */
export function isLockableUpcomingGame(game, opts = {}) {
  if (!game?.sport || !game?.a || !game?.b) return false;
  if (isIfNecessaryPlaceholder(game)) return false;
  if (isTerminalGameStatus(game.status)) return false;
  if (isLiveGameStatus(game.status)) return false;
  if (isStaleFinishedGame(game, opts)) return false;
  if (!isPreGameStatus(game.status)) return false;

  const now = opts.now ?? Date.now();
  const graceMs = opts.graceMs ?? 5 * 60_000;
  const start = gameStartMs(game);
  if (start == null) return false;
  if (start < now - graceMs) return false;

  const { startToday, endWindow } = upcomingWindowBounds(opts.windowDays ?? 7);
  if (start < startToday.getTime() || start > endWindow.getTime()) return false;

  if (hasFinalScores(game)) return false;
  return true;
}

/** @param {object[]} games */
export function filterLockableUpcomingGames(games, opts = {}) {
  if (!Array.isArray(games)) return [];
  return games.filter((game) => isLockableUpcomingGame(game, opts));
}
